import { Injectable } from '@angular/core';
import { addDays } from '@em-helpers';

import { environment } from '../../../../environments/environment';
import { FeatureHighlightKey, getFeatureHighlights } from './feature-highlight.config';
import { FeatureHighlightConfig } from './feature-highlight.model';

@Injectable({
  providedIn: 'root',
})
export class FeatureHighlightService {
  private static readonly storageKey = 'feature-highlight-dismissed';
  private static readonly expiryDays = 60;

  constructor() {
    this.cleanupExpired();
  }

  public getConfig(key: FeatureHighlightKey): FeatureHighlightConfig | undefined {
    const config = getFeatureHighlights()[key];
    if (this.isExpired(config.addedDate, config.expiryDays)) {
      return undefined;
    }
    if (this.isDismissed(key)) {
      return undefined;
    }
    return config;
  }

  public dismiss(key: FeatureHighlightKey): void {
    const dismissed = this.getDismissedSet();
    dismissed.add(key);
    this.saveDismissedSet(dismissed);
  }

  private isDismissed(key: FeatureHighlightKey): boolean {
    return this.getDismissedSet().has(key);
  }

  private getDismissedSet(): Set<string> {
    try {
      const raw = localStorage.getItem(FeatureHighlightService.storageKey);
      if (!raw) {
        return new Set();
      }
      const parsed: string[] = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return new Set();
      }
      return new Set(parsed.filter((k) => k in getFeatureHighlights()));
    } catch {
      return new Set();
    }
  }

  private saveDismissedSet(dismissed: Set<string>): void {
    try {
      localStorage.setItem(FeatureHighlightService.storageKey, JSON.stringify([...dismissed]));
    } catch (error) {
      console.warn('Failed to persist feature highlight dismissed state', error);
    }
  }

  private isExpired(addedDate: string, expiryDays?: number): boolean {
    const expiryDate = addDays(
      new Date(addedDate),
      expiryDays ?? FeatureHighlightService.expiryDays,
    );
    return new Date() >= expiryDate;
  }

  private cleanupExpired(): void {
    const dismissed = this.getDismissedSet();
    let changed = false;

    const highlights = getFeatureHighlights();

    for (const key of Object.keys(highlights) as FeatureHighlightKey[]) {
      const config = highlights[key];
      if (this.isExpired(config.addedDate, config.expiryDays)) {
        if (environment.type === 'local') {
          console.warn(
            `[FeatureHighlight] Highlight "${key}" has expired. Please remove the highlight code.`,
          );
        }
        if (dismissed.has(key)) {
          dismissed.delete(key);
          changed = true;
        }
      }
    }

    if (changed) {
      this.saveDismissedSet(dismissed);
    }
  }
}
